import type { ChatMessage } from "api-client";
import { type TFunction } from "i18next";
import {
  isLexicalJsonState,
  lexicalStateToString,
  type ChatAction,
  type ChatErrorCopyId,
} from "utils";

export function formatLastMessage(
  lastMessage: ChatMessage | null,
  t: TFunction,
): string {
  if (!lastMessage) {
    return t("noMessages");
  }

  const text = isLexicalJsonState(lastMessage.content)
    ? lexicalStateToString(lastMessage.content).trim()
    : "";

  const body =
    text.length > 0 ? text : formatAttachmentsPreview(lastMessage, t);

  if (body.length === 0) {
    return t("noMessages");
  }

  const who = lastMessage.wasSentBySameUser
    ? t("you")
    : lastMessage.sender.name;

  return `${who}: ${body}`;
}

function formatAttachmentsPreview(message: ChatMessage, t: TFunction): string {
  const attachments = message.attachments;

  if (!attachments || attachments.length === 0) {
    return "";
  }

  if (attachments.length === 1) {
    const mime = attachments[0].mimeType;

    if (mime.startsWith("image/")) return t("attachmentPreviewPhoto");

    if (mime.startsWith("video/")) return t("attachmentPreviewVideo");

    return t("attachmentPreviewFile");
  }

  return t("attachmentPreviewCount", { count: attachments.length });
}

type WebTranslate = (key: string, defaultValue: string) => string;

interface WebChatErrorToast {
  title: string;
  description: string;
}

export function chatErrorIdToWebToast(
  t: WebTranslate,
  copyId: ChatErrorCopyId,
  _action: ChatAction,
): WebChatErrorToast {
  switch (copyId) {
    case "edit.windowExpired":
      return {
        title: t(
          "chatMessageEdit.editWindowExpiredTitle",
          "Edit window has expired",
        ),
        description: t(
          "chatMessageEdit.editWindowExpiredDescription",
          "You can only edit a message within 15 minutes of sending it.",
        ),
      };
    case "edit.forbidden":
      return {
        title: t(
          "chatMessageEdit.editForbiddenTitle",
          "Can't edit this message",
        ),
        description: t(
          "chatMessageEdit.editForbiddenDescription",
          "The message can no longer be edited.",
        ),
      };
    case "edit.empty":
      return {
        title: t("chatMessageEdit.errorEmpty", "Message cannot be empty"),
        description: t(
          "chatMessageEdit.errorEmptyDescription",
          "An edited message cannot be empty. Type something or cancel.",
        ),
      };
    case "edit.notFound":
    case "edit.generic":
      return {
        title: t("chatMessageEdit.errorTitle", "Could not save edit"),
        description: t(
          "chatMessageEdit.errorDescription",
          "Your message was restored. Try again.",
        ),
      };
    case "delete.forbidden":
      return {
        title: t(
          "chatMessageDelete.deleteForbiddenTitle",
          "Can't delete this message",
        ),
        description: t(
          "chatMessageDelete.deleteForbiddenDescription",
          "Only the sender can delete this message.",
        ),
      };
    case "delete.notFound":
    case "delete.generic":
      return {
        title: t("chatMessageDelete.errorTitle", "Could not delete message"),
        description: t(
          "chatMessageDelete.errorDescription",
          "Please try again.",
        ),
      };
    case "send.chatGone":
      return {
        title: t("chatMessageSend.chatGoneTitle", "Chat unavailable"),
        description: t(
          "chatMessageSend.chatGoneDescription",
          "This conversation is no longer available.",
        ),
      };
    case "send.attachmentOwnershipDenied":
      return {
        title: t(
          "chatMessageSend.attachmentOwnershipTitle",
          "Attachment rejected",
        ),
        description: t(
          "chatMessageSend.attachmentOwnershipDescription",
          "One of the files isn't yours to attach. Remove it and try again.",
        ),
      };
    case "send.attachmentLimitExceeded":
      return {
        title: t(
          "chatMessageSend.attachmentLimitTitle",
          "Too many attachments",
        ),
        description: t(
          "chatMessageSend.attachmentLimitDescription",
          "Remove some attachments and try again.",
        ),
      };
    case "send.messageIdConflict":
      return {
        title: t(
          "chatMessageSend.messageIdConflictTitle",
          "Couldn't send message",
        ),
        description: t(
          "chatMessageSend.messageIdConflictDescription",
          "This message has already been sent. Reload to see it.",
        ),
      };
    case "send.generic":
      return {
        title: t("chatMessageSend.errorTitle", "Couldn't send message"),
        description: t("chatMessageSend.errorDescription", "Please try again."),
      };
  }
}

interface BlobRevokeStore {
  getMessageById: (
    id: string,
  ) => { attachments: { url: string | null }[] } | undefined;
  chats: ReadonlyArray<{
    lastMessage: { attachments: { url: string | null }[] } | null;
  }>;
}

export function revokeReplacedBlobs(
  store: BlobRevokeStore,
  messageId: string,
  candidates: readonly string[],
): void {
  if (candidates.length === 0) {
    return;
  }

  const stillRendered = new Set<string>();
  const current = store.getMessageById(messageId);

  if (current) {
    for (const attachment of current.attachments) {
      if (attachment.url !== null) {
        stillRendered.add(attachment.url);
      }
    }
  }

  for (const chat of store.chats) {
    if (!chat.lastMessage) continue;

    for (const attachment of chat.lastMessage.attachments) {
      if (attachment.url !== null) {
        stillRendered.add(attachment.url);
      }
    }
  }

  for (const url of candidates) {
    if (!stillRendered.has(url)) {
      URL.revokeObjectURL(url);
    }
  }
}
